import type { AudioOnset, ChartAudio, ChartPalette } from '../chart.js';
import { buildVariantTitle } from '../chart.js';
import {
  HIT_SAMPLE_SETS,
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
  type CircleHitObject,
  type CirclesChart,
  type HitSampleSet,
  type KiaiSection,
  type SliderHitData,
} from '../circlesChart.js';
import {
  CIRCLES_VARIANTS,
  type CirclesVariant,
  type Difficulty,
  type Variant,
} from '../gameTypes.js';
import {
  computeFillerBeats,
  fillerPhase,
  FILLER_SECTION_BEATS,
  type FillerSpan,
} from '../fillerNotes.js';
import type { SliderCurveType, Vec2 } from '../osuCurves.js';
import { buildCirclesChart, CIRCLES_VARIANT_TUNING } from './circlesFromOnsets.js';

/**
 * Uniform inward squeeze applied to imported osu hit-object positions
 * before they reach the engine + renderer. osu! maps habitually place
 * circles right at the playfield edges (x=0, x=512, y=0, y=384 all
 * appear in our shipped beatmaps); rendered against our letterboxed
 * canvas that puts the centre of the circle exactly on the playfield
 * frame, with half the body clipping into the border. Squeezing all
 * positions ~14% toward the playfield centre pulls the worst-case
 * corner from (0, 0) → (36, 27) — circle bodies still poke a few
 * units past the frame at the most extreme positions, but no centre
 * sits *on* the edge any more, which was the visually-jarring case.
 * Relative flow is preserved exactly (every gap shrinks by the same
 * factor, so a mapper's pattern reads the same — just slightly more
 * compact).
 *
 * Value picked so the corner offset (36, 27) is roughly two thirds
 * of a normal-variant circle radius — enough breathing room to read
 * the circle as "inside the playfield" without compressing the
 * patterns so much that long diagonals lose their punch.
 *
 * Synthesised charts (`layoutHitObjects` in `./circlesFromOnsets.js`)
 * apply their own `PLAYFIELD_PAD` and aren't affected by this
 * constant.
 *
 * Stacking detection (`getStackOffsets` in `circlesStacking.ts`)
 * uses a 6-osu-unit overlap threshold. Uniform scaling preserves
 * exact-same-xy stacks (distance 0 stays 0) and shrinks larger gaps
 * proportionally. A near-threshold pair at pre-squeeze distance ~7
 * could cross into stacking post-squeeze; in practice osu! mappers
 * place either exact stacks or distinct circles at much larger
 * gaps, so the band is empty.
 */
const IMPORTED_PLAYFIELD_SQUEEZE = 0.86;

function squeezeImportedX(x: number): number {
  const centre = OSU_PLAYFIELD_WIDTH / 2;
  return Math.round(centre + (x - centre) * IMPORTED_PLAYFIELD_SQUEEZE);
}

function squeezeImportedY(y: number): number {
  const centre = OSU_PLAYFIELD_HEIGHT / 2;
  return Math.round(centre + (y - centre) * IMPORTED_PLAYFIELD_SQUEEZE);
}

/**
 * Slider geometry + derived timing as persisted in
 * `imported/<slug>-<variant>.circles.json`. Curve geometry coords are
 * raw osu playfield units (pre-squeeze); we apply the same uniform
 * inward squeeze to control points that we apply to circle (x, y) so
 * a slider stays inside the rendered playfield frame.
 *
 * `curveType` is typed as `string` because TS widens JSON imports —
 * the writer (`RebuildCirclesFromOsu.ts`) only emits `'L' | 'B' | 'P'
 * | 'C'`. Bad/unknown values fall back to linear in
 * {@link buildHitObjectsFromImported}.
 */
export interface ImportedSliderEntry {
  curveType: string;
  controlPoints: ReadonlyArray<{ x: number; y: number }>;
  length: number;
  slides: number;
  endBeat: number;
  tickBeats: ReadonlyArray<number>;
  repeatBeats: ReadonlyArray<number>;
}

/**
 * Wire format we persist in `imported/<slug>-<variant>.circles.json`.
 *
 * `kind` is typed as `string` (not the literal union) because TS
 * widens JSON imports — the runtime values are always one of the
 * known literals (the writer is `RebuildCirclesFromOsu.ts` and we
 * control it), but the import-side type can't reflect that without a
 * `with { type: 'json' }` schema. We narrow at the boundary in
 * {@link buildHitObjectsFromImported}.
 */
export interface ImportedCircleEntry {
  beat: number;
  x: number;
  y: number;
  kind: string;
  newCombo: boolean;
  comboIndex: number;
  /**
   * osu! hitsound addition bitmask (whistle|finish|clap) for this
   * object's head, as written by `RebuildCirclesFromOsu.ts`. Absent on
   * plain notes. Carried verbatim onto the {@link CircleHitObject} at
   * load (and preserved when a slider is stripped to a click), then
   * read by the session to layer the percussive accent on hit.
   */
  hitSound?: number;
  spinnerEndBeat?: number;
  slider?: ImportedSliderEntry;
}

export interface ImportedCircleBundle {
  slug: string;
  variant: string;
  circles: ReadonlyArray<ImportedCircleEntry>;
  /**
   * Mapper-defined `[Colours]` palette from the source `.osu`. Per-set
   * data (osu! mappers conventionally share one combo palette across
   * a set's diffs), so it's duplicated across the three variant JSONs
   * for that set — keeps the wire format flat without introducing a
   * separate per-set file. Optional: legacy bundles or hand-written
   * tests omit it and the renderer falls back to its built-in cycle.
   */
  comboColours?: ReadonlyArray<string>;
  /**
   * Beat ranges where the source `.osu`'s timing points flagged kiai
   * mode active, derived per-variant from each diff's own
   * `[TimingPoints]` (the kiai bit can legitimately differ across
   * diffs of the same set, so this is NOT collapsed to set-level the
   * way `comboColours` is).
   *
   * Beats are in the variant's own beat space (matches `circles[].beat`
   * — same `(timeMs - offsetMs) / beatLengthMs` derivation), so the
   * renderer can read them directly without per-variant rescaling.
   * Optional: legacy bundles or maps without kiai omit it.
   */
  kiaiSections?: ReadonlyArray<KiaiSection>;
  /**
   * `[Difficulty].SliderMultiplier` from the source `.osu`. Drives the
   * slider velocity formula at the chart-rebuild step; persisted only
   * so the runtime can echo it back if a future tool wants to redo
   * slider timing without re-parsing the .osu. Per-variant because
   * each diff in a set legitimately ships its own SliderMultiplier
   * (Extreme often runs 1.6-1.8 vs Normal at 1.0-1.2). Omitted when
   * the variant has no sliders.
   */
  sliderMultiplier?: number;
  /**
   * `[Difficulty].SliderTickRate` from the source `.osu`. See
   * `sliderMultiplier` above. Omitted when no sliders.
   */
  sliderTickRate?: number;
  /**
   * `[General].SampleSet` skin bank governing the timbre of this
   * diff's hitsound additions. Typed as `string` because TS widens
   * JSON imports; narrowed to {@link HitSampleSet} at the boundary in
   * {@link assembleImportedCirclesChart}. Omitted (⇒ `'normal'`) when
   * the source used the default bank or carries no additions.
   */
  hitSampleSet?: string;
}

/**
 * Wire format for the per-track onsets file.
 * Anchored to the *normal* variant's bpm/offsetMs so other variants
 * can rescale into their own beat-space at load time without needing
 * three separate onset JSONs.
 */
export interface ImportedOnsetsFile {
  slug: string;
  bpm: number;
  offsetMs: number;
  durationSec: number;
  onsets: ReadonlyArray<{ beat: number; kind: string; intensity: number }>;
}

/**
 * Narrow the imported curve-type string to the engine's literal union.
 * Unknown values fall back to linear ('L') — same defensive policy as
 * `kind` narrowing: bad data shouldn't crash chart load.
 */
function narrowCurveType(raw: string): SliderCurveType {
  if (raw === 'B' || raw === 'P' || raw === 'C') {
    return raw;
  }
  return 'L';
}

/**
 * Narrow the imported `hitSampleSet` string to {@link HitSampleSet}.
 * Returns `undefined` for absent OR `'normal'` (the runtime default —
 * no point threading it through) and for any unknown value, matching
 * the defensive policy used for `kind` / `curveType` narrowing.
 */
function narrowHitSampleSet(raw: string | undefined): HitSampleSet | undefined {
  if (raw === undefined || raw === 'normal') {
    return undefined;
  }
  return (HIT_SAMPLE_SETS as ReadonlyArray<string>).includes(raw)
    ? (raw as HitSampleSet)
    : undefined;
}

/**
 * Re-narrow the imported `kind` string to the literal union the engine
 * expects. Anything outside the known set falls back to `'circle'` —
 * the writer only emits known literals, but unknown strings shouldn't
 * crash load. Slider entries without a `slider` payload also degrade
 * to plain circles (the runtime needs the geometry to animate them).
 */
function buildHitObjectsFromImported(
  circles: ReadonlyArray<ImportedCircleEntry>,
): CircleHitObject[] {
  return circles.map((c, i) => {
    const isSpinner = c.kind === 'spinner';
    const isSlider = c.kind === 'slider' && c.slider !== undefined;
    const obj: CircleHitObject = {
      id: `c-${i.toString().padStart(4, '0')}`,
      beat: c.beat,
      x: squeezeImportedX(c.x),
      y: squeezeImportedY(c.y),
      kind: isSpinner ? 'spinner' : isSlider ? 'slider' : 'circle',
      newCombo: c.newCombo,
      comboIndex: c.comboIndex,
    };
    if (c.spinnerEndBeat !== undefined) {
      obj.spinnerEndBeat = c.spinnerEndBeat;
    }
    if (c.hitSound !== undefined && c.hitSound !== 0) {
      obj.hitSound = c.hitSound;
    }
    if (isSlider && c.slider) {
      const sliderEntry = c.slider;
      // Same uniform squeeze applied to control points as to head xy
      // — keeps the slider's curve geometry consistent with the
      // squeezed head position. Without this, the head would land at
      // the squeezed (x, y) but the curve would trace through raw
      // playfield coords and detach from the head.
      const squeezedControlPoints: Vec2[] = sliderEntry.controlPoints.map((p) => ({
        x: squeezeImportedX(p.x),
        y: squeezeImportedY(p.y),
      }));
      const slider: SliderHitData = {
        curveType: narrowCurveType(sliderEntry.curveType),
        controlPoints: squeezedControlPoints,
        // length is in osu's path-length units (already a scalar arc
        // length, not coordinates) so the squeeze factor would
        // distort it relative to the squeezed control points. We
        // scale it by the same factor so the slider ball reaches
        // the same proportion along the squeezed curve.
        length: sliderEntry.length * IMPORTED_PLAYFIELD_SQUEEZE,
        slides: sliderEntry.slides,
        endBeat: sliderEntry.endBeat,
        tickBeats: [...sliderEntry.tickBeats],
        repeatBeats: [...sliderEntry.repeatBeats],
      };
      obj.slider = slider;
    }
    return obj;
  });
}

/**
 * Vertical bands for filler circles, expressed in raw osu! playfield
 * units (pre-squeeze). They straddle the playfield midline so Waves —
 * which reads `y` to choose a ceiling vs floor spike — gets alternating
 * dive/climb bursts, while Circles gets a gentle vertical bounce. Kept
 * well clear of the edges so neither the inward squeeze nor the approach
 * ring clips the frame.
 */
const FILLER_Y_HIGH = 110;
const FILLER_Y_LOW = 274;
const FILLER_X_CENTRE = OSU_PLAYFIELD_WIDTH / 2;
/** Half-width of the left→right sweep the filler traces across each on-run. */
const FILLER_X_SWING = 105;

/**
 * Position a filler circle for a given (integer) beat and its precomputed
 * phrase phase. One vertical side per 8-beat phrase (so a run of same-side
 * spikes in Waves is trivially threadable — side switches only happen
 * across the 4-beat rest, which is wide enough to cross the corridor),
 * with a gentle left→right sweep across the phrase's four on-beats.
 */
function fillerCirclePlacement(beat: number, phase: number): { x: number; y: number } {
  const section = Math.floor(beat / FILLER_SECTION_BEATS);
  const y = section % 2 === 0 ? FILLER_Y_HIGH : FILLER_Y_LOW;
  const x = FILLER_X_CENTRE + ((phase - 1.5) / 1.5) * FILLER_X_SWING;
  return { x: Math.round(x), y };
}

/**
 * Append beat-grid filler circles to an imported circle list, filling
 * any genuinely-long empty gap (see {@link computeFillerBeats}). Shared
 * by Circles (the entries become hit objects) and Waves (the entries
 * become spike obstacles): for a GIVEN source bundle both modes derive
 * identical filler. They don't necessarily feed it the same bundle per
 * variant, though — Circles applies its tier-shift (e.g. the extreme tier
 * reads the `hard` diff) while Waves reads the same-named diff — so
 * same-named variants needn't match beat-for-beat. Pure: the input is
 * treated as read-only and a new, beat-sorted array is returned.
 */
export function withFillerCircleEntries(
  circles: ReadonlyArray<ImportedCircleEntry>,
  totalBeats: number,
): ImportedCircleEntry[] {
  const occupied: FillerSpan[] = circles.map((c) => {
    if (c.kind === 'spinner') {
      return { startBeat: c.beat, endBeat: c.spinnerEndBeat ?? c.beat };
    }
    if (c.kind === 'slider' && c.slider) {
      return { startBeat: c.beat, endBeat: c.slider.endBeat };
    }
    return { startBeat: c.beat, endBeat: c.beat };
  });
  const fillerBeats = computeFillerBeats(occupied);
  if (fillerBeats.length === 0) {
    return [...circles];
  }
  const filler: ImportedCircleEntry[] = [];
  for (const beat of fillerBeats) {
    if (beat > totalBeats) {
      continue;
    }
    const phase = fillerPhase(beat);
    const { x, y } = fillerCirclePlacement(beat, phase);
    filler.push({
      beat,
      x,
      y,
      kind: 'circle',
      // One combo group per phrase, new combo on its downbeat. The
      // renderer cycles comboIndex through the palette (mod its length),
      // so the raw phrase number is fine — no need to pre-mod to a fixed
      // cycle length (which would just cap filler to the first colours).
      newCombo: phase === 0,
      comboIndex: Math.floor(beat / FILLER_SECTION_BEATS),
    });
  }
  return [...circles, ...filler].sort((a, b) => a.beat - b.beat);
}

/**
 * Collapse every slider in a hit-object list down to a single click at
 * the slider's head — the slider body, ticks, repeats and tail are
 * dropped so only a plain hit circle remains at the head's beat/position.
 * Circles and spinners pass through untouched.
 *
 * This is what makes the circles `normal` tier a slider-free on-ramp: it
 * reuses the imported easy/normal beatmap verbatim but turns each "click
 * and drag" into a "just click", so a new player learns aim + timing
 * before the `hard` tier reintroduces the very same sliders.
 */
function stripSlidersToClicks(hitObjects: CircleHitObject[]): CircleHitObject[] {
  return hitObjects.map((obj) => {
    if (obj.kind !== 'slider') {
      return obj;
    }
    const { slider: _slider, spinnerEndBeat: _spinnerEndBeat, ...rest } = obj;
    return { ...rest, kind: 'circle' } satisfies CircleHitObject;
  });
}

/**
 * Centre-to-centre distance (osu units) below which two beginner-tier
 * taps read as the same target. Set to the normal hit-circle radius: at
 * this spacing one circle's centre sits on the other's rim and the
 * engine's tap radius check ({@link
 * import('../engine/circles/core.js')} accepts a tap within
 * `circleRadius`) would accept either, so a first-time player genuinely
 * can't tell there are two distinct circles.
 */
export const BEGINNER_STACK_DISTANCE_OSU = CIRCLES_VARIANT_TUNING.normal.circleRadius;

/**
 * Beat gap below which a second tap lands "immediately" after the
 * first. 0.5 beat covers the 16th-note `X.25 → X.5` click-then-slider
 * stacks osu! mappers use (the slider-free normal tier collapses both
 * to clicks), without touching steady same-spot pulses a full beat
 * apart — their approach rings don't pile up the same way.
 */
export const BEGINNER_STACK_WINDOW_BEATS = 0.5;

/**
 * Drop tap targets that sit on top of (within {@link
 * BEGINNER_STACK_DISTANCE_OSU} of) their immediate predecessor inside
 * {@link BEGINNER_STACK_WINDOW_BEATS}. osu! mappers routinely stack a
 * circle and a slider head on the same coordinate a 16th-note apart;
 * the slider-free `normal` tier collapses both to clicks, leaving an
 * invisible rapid double-tap on one spot that a first-time player reads
 * as a single circle and then "inexplicably" misses.
 *
 * Applied only to the beginner on-ramp (the `normal` tier of ≤3-dot
 * songs — see {@link assembleImportedCirclesChart}). Harder tiers and
 * songs keep their stacks (osu! parity), made readable by the
 * renderer's diagonal stack offset (`circlesStacking.ts`).
 *
 * Pure: the input is treated as read-only and a new beat-sorted array
 * is returned. Each circle is compared against the previous KEPT
 * circle, so a tightly-coincident run collapses to one tap per stack
 * window. A spinner breaks the chain (it is centre-rendered, not a tap
 * target, so it neither anchors the overlap test nor lets circles
 * either side of it be treated as consecutive).
 *
 * A dropped circle's `newCombo` flag is carried onto the next kept
 * circle so the combo group (and its within-combo counter) still
 * restarts at that musical position instead of silently extending the
 * previous group.
 */
export function dropImmediateOverlappingClicks(
  hitObjects: CircleHitObject[],
): CircleHitObject[] {
  const sorted = [...hitObjects].sort((a, b) => a.beat - b.beat);
  const distSqLimit = BEGINNER_STACK_DISTANCE_OSU * BEGINNER_STACK_DISTANCE_OSU;
  const kept: CircleHitObject[] = [];
  let lastKeptCircle: CircleHitObject | null = null;
  let carryNewCombo = false;
  for (const obj of sorted) {
    if (obj.kind !== 'circle') {
      kept.push(obj);
      lastKeptCircle = null;
      continue;
    }
    if (lastKeptCircle !== null) {
      const closeInTime = obj.beat - lastKeptCircle.beat <= BEGINNER_STACK_WINDOW_BEATS;
      const dx = obj.x - lastKeptCircle.x;
      const dy = obj.y - lastKeptCircle.y;
      const overlapping = dx * dx + dy * dy <= distSqLimit;
      if (closeInTime && overlapping) {
        carryNewCombo = carryNewCombo || obj.newCombo;
        continue;
      }
    }
    const next = carryNewCombo && !obj.newCombo ? { ...obj, newCombo: true } : obj;
    carryNewCombo = false;
    kept.push(next);
    lastKeptCircle = next;
  }
  return kept;
}

/**
 * Re-express onsets stored in the anchor variant's beat space into a
 * different variant's beat space. The audio file is shared across
 * variants — onsets describe *physical* moments in the audio buffer,
 * and only the chart's beat grid (BPM, offset) differs per variant.
 *
 * Onsets that would land before the variant's offset are dropped so
 * the engine's "ms < 0" guard never fires. Output beats are rounded
 * to 0.001 to keep generated files small.
 */
function rescaleOnsets(
  anchorBpm: number,
  anchorOffsetMs: number,
  onsets: ReadonlyArray<{ beat: number; kind: string; intensity: number }>,
  toBpm: number,
  toOffsetMs: number,
): AudioOnset[] {
  const out: AudioOnset[] = [];
  for (const o of onsets) {
    const realMs = (o.beat * 60000) / anchorBpm + anchorOffsetMs;
    if (realMs < toOffsetMs) {
      continue;
    }
    const beat = ((realMs - toOffsetMs) * toBpm) / 60000;
    // Re-narrow the kind. AudioOnset['kind'] is currently
    // 'kick' | 'snare' | 'lead' (see chart.ts). The writer only emits
    // those literals; an unknown string falls back to 'lead' so a
    // future kind written by some other tool doesn't crash the engine.
    const kind: AudioOnset['kind'] =
      o.kind === 'kick' || o.kind === 'snare' ? o.kind : 'lead';
    out.push({
      beat: Math.round(beat * 1000) / 1000,
      kind,
      intensity: o.intensity,
    });
  }
  return out;
}

export interface PerVariantTimingArg {
  bpm: number;
  offsetMs: number;
  totalBeats: number;
}

export interface AssembleImportedCirclesArgs {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  palette: ChartPalette;
  audioSrc: string;
  attribution: string;
  mapper: string;
  /**
   * Onsets file imported from `audio/<slug>.onsets.json`. The bpm /
   * offsetMs on this file is the **anchor** (normal-variant) timing
   * the onsets were extracted against; per-variant rescaling happens
   * here.
   */
  anchorOnsets: ImportedOnsetsFile;
  /**
   * The three imported osu! diffs from
   * `imported/<slug>-{normal,hard,extreme}.circles.json`. These are the
   * *source* beatmaps, not the final playable tiers: the four circles
   * tiers are derived from them by {@link CIRCLES_TIER_SOURCE}.
   */
  imported: Record<Variant, ImportedCircleBundle>;
  /**
   * Per-diff chart timing. Each osu! diff can declare its own first
   * uninherited timing point (different bpm grid or offset), and we
   * honour that so the source mapper's beat math is faithful. Keyed by
   * source diff (matches {@link imported}); each circles tier inherits
   * the timing of the diff it is built from. Engine-driven playback uses
   * `chart.bpm` directly — see notes on {@link CirclesChart.bpm}.
   */
  timing: Record<Variant, PerVariantTimingArg>;
}

/**
 * Which imported osu! diff feeds each playable circles tier. The four
 * tiers are the three source diffs shifted up one rung, with a new
 * slider-free `normal` on-ramp grafted underneath:
 *
 *   normal  ← easy/normal diff, sliders stripped to clicks (on-ramp)
 *   hard    ← easy/normal diff, verbatim (sliders reintroduced)
 *   extreme ← hard diff, verbatim
 *   insane  ← hardest diff, verbatim
 *
 * `normal` is the only tier with `stripSliders`, so it is also the only
 * tier guaranteed slider-free — which is exactly the signal the play
 * page uses to decide whether to run the slider tutorial first.
 */
const CIRCLES_TIER_SOURCE: Record<
  CirclesVariant,
  { diff: Variant; stripSliders: boolean }
> = {
  normal: { diff: 'normal', stripSliders: true },
  hard: { diff: 'normal', stripSliders: false },
  extreme: { diff: 'hard', stripSliders: false },
  insane: { diff: 'extreme', stripSliders: false },
};

/**
 * Build all four playable CirclesChart tiers for an osu!-imported track
 * from its three source diffs, applying the {@link CIRCLES_TIER_SOURCE}
 * shift (and the slider-free `normal` on-ramp).
 *
 * Replaces ~95 lines of identical boilerplate that used to sit in each
 * generated `<NN>-<slug>.ts` file (raw circle narrowing, onset
 * rescaling, audio assembly, makeChart). Each generated file is now
 * just constants + a single call to this helper, so any future fix to
 * the import-time math (rescaling, type narrowing, validation tweaks)
 * lands in exactly one place.
 */
export function assembleImportedCirclesChart(
  args: AssembleImportedCirclesArgs,
): Record<CirclesVariant, CirclesChart> {
  const {
    baseSlug,
    baseTitle,
    description,
    difficulty,
    palette,
    audioSrc,
    attribution,
    mapper,
    anchorOnsets,
    imported,
    timing,
  } = args;
  // Combo colours are set-level in osu! — `RebuildCirclesFromOsu.ts`
  // writes the same array into all variant JSONs for a track. Read once
  // from the anchor (Normal) bundle and pass to every tier; spread to
  // drop the `readonly` marker since {@link CirclesChart.comboColours}
  // declares mutable `string[]`. `buildCirclesChart` filters empty
  // arrays before they reach the chart, so we don't guard length here.
  const setComboColours = imported.normal.comboColours
    ? [...imported.normal.comboColours]
    : undefined;
  const out = {} as Record<CirclesVariant, CirclesChart>;
  for (const variant of CIRCLES_VARIANTS) {
    const { diff, stripSliders } = CIRCLES_TIER_SOURCE[variant];
    const sourceBundle = imported[diff];
    // Each tier inherits the beat grid of the source diff it's built
    // from, so onsets + bpm stay aligned to the notes the player sees.
    const t = timing[diff];
    const audio: ChartAudio = {
      src: audioSrc,
      bpm: t.bpm,
      offsetMs: t.offsetMs,
      durationSec: anchorOnsets.durationSec,
      attribution,
      onsets: rescaleOnsets(
        anchorOnsets.bpm,
        anchorOnsets.offsetMs,
        anchorOnsets.onsets,
        t.bpm,
        t.offsetMs,
      ),
    };
    // Kiai is per-diff (same map can chart different kiai across diffs —
    // half-time Easy diffs sometimes drop kiai entirely). Spread to drop
    // readonly + give each tier its own array identity so the chart's
    // kiaiSections aren't aliased back into the source bundle.
    const importedKiai = sourceBundle.kiaiSections;
    const kiaiSections =
      importedKiai && importedKiai.length > 0 ? importedKiai.map((s) => ({ ...s })) : undefined;
    // Backfill long empty stretches (intros, breakdowns) with on-the-beat
    // filler before narrowing to hit objects. Filler entries are plain
    // circles, so the slider-strip pass below is a no-op on them.
    const filledCircles = withFillerCircleEntries(sourceBundle.circles, t.totalBeats);
    let hitObjects = stripSliders
      ? stripSlidersToClicks(buildHitObjectsFromImported(filledCircles))
      : buildHitObjectsFromImported(filledCircles);
    // Beginner on-ramp: the slider-free `normal` tier of an easy
    // (≤3-dot) song must never present two tap targets stacked on the
    // same spot in immediate succession — a brand-new player reads them
    // as one circle and misses the hidden rapid follow-up. De-stack by
    // dropping the buried tap. Harder tiers / songs keep osu! stacks
    // (offset visually by the renderer).
    if (variant === 'normal' && difficulty <= 3) {
      hitObjects = dropImmediateOverlappingClicks(hitObjects);
    }
    // sliderMultiplier / sliderTickRate come from the source diff's
    // [Difficulty] section. The slider-free `normal` tier drops the pair
    // (no sliders remain to tune, and the validator rejects slider
    // tuning on a slider-less chart); the others echo the source diff's
    // values when present.
    const sliderMultiplier = stripSliders ? undefined : sourceBundle.sliderMultiplier;
    const sliderTickRate = stripSliders ? undefined : sourceBundle.sliderTickRate;
    // Hitsound timbre is per-source-diff (each tier inherits the bank
    // of the diff it's built from). Narrowed at this boundary; the
    // slider-strip pass keeps each note's `hitSound`, so even the
    // normal on-ramp plays its accents.
    const hitSampleSet = narrowHitSampleSet(sourceBundle.hitSampleSet);
    out[variant] = buildCirclesChart({
      baseSlug,
      variant,
      title: buildVariantTitle(baseTitle, variant),
      description,
      bpm: t.bpm,
      difficulty,
      totalBeats: t.totalBeats,
      palette,
      audio,
      hitObjects,
      mapper,
      ...(setComboColours !== undefined ? { comboColours: setComboColours } : {}),
      ...(kiaiSections !== undefined ? { kiaiSections } : {}),
      ...(sliderMultiplier !== undefined ? { sliderMultiplier } : {}),
      ...(sliderTickRate !== undefined ? { sliderTickRate } : {}),
      ...(hitSampleSet !== undefined ? { hitSampleSet } : {}),
    });
  }
  return out;
}
